import moment from 'moment';

/** Cell colours — mirror BE CERTIFICATE_CELL_COLORS (IEC workbook Rev 8.4). */
export const CERTIFICATE_CELL_COLORS = {
  dropdown: '#4A90D9',
  userEntry: '#FFF59D',
  auto: '#E0E0E0',
  pass: '#4CAF50',
  fail: '#F44336',
  incomplete: '#FF9800',
  heading: '#FFFFFF',
};

export const AW_CERTIFICATE_STATUS = {
  DRAFT: 'Draft',
  AWAITING_REVIEW: 'Awaiting Review',
  ISSUED: 'Issued',
  SUPERSEDED: 'Superseded',
};

export const AW_OVERALL_RESULT = {
  INCOMPLETE: 'Incomplete',
  PASS: 'Pass',
  FAIL: 'Fail',
};

export const AW_PARAMETERS = ['Current', 'Voltage', 'Wire feed speed'];

export const AW_VALUE_CLASSIFICATIONS = [
  'Displayed value - Digital/Analogue meter',
  'Set value - Physical units',
  'Set control - Arbitrary units',
  'Not tested',
];

export const AW_METHODS = {
  CALIBRATION: 'Calibration',
  VALIDATION: 'Validation',
  CONSISTENCY: 'Consistency test',
  NOT_TESTED: 'Not tested',
};

/** BE enum — exact strings */
export const AW_EQUIPMENT_TYPES = ['Power source', 'Wire feeder', 'Other'];

export const AW_GRADES = ['Standard', 'Precision'];

export const AW_WFS_UNITS = ['m/min', 'in/min'];

export const AW_AUTHORISATION_ROLES = {
  TECHNICIAN: 'Technician',
  SUPERVISOR: 'Supervisor',
};

/** Certificate form section headings — Title Case for UI consistency. */
export const AW_CERTIFICATE_SECTIONS = {
  HEADER: 'Certificate Details',
  EQUIPMENT_PROFILE: 'Equipment Profile (Register)',
  VERIFICATION_CONFIG: 'Verification Configuration',
  REFERENCE_INSTRUMENTS: 'Reference Instruments',
  PROCEDURAL: 'Procedural Confirmations',
  REMARKS: 'Remarks',
  AUTHORISATION: 'Authorisation',
};

/** Admin modal section headings — Title Case. */
export const AW_MODAL_SECTIONS = {
  PROFILE_DETAILS: 'Profile Details',
  RATING_PLATE_RANGES: 'Rating Plate Ranges',
  CERTIFICATE_DEFAULTS: 'Certificate Defaults',
  CRITERION: 'Criteria Details',
  TOLERANCE: 'Tolerance',
  DISPLAY: 'Display',
};

/** Shared form field labels — Title Case across all AW tabs. */
export const AW_FORM_LABELS = {
  certificateNo: 'Certificate No.',
  project: 'Project',
  crcSite: 'CRC Site',
  siteName: 'Site Name',
  equipmentProfile: 'Equipment Profile',
  manualOverride: 'Manual Override',
  ratingPlateDiffers: 'Rating Plate Differs',
  serialNumber: 'Serial Number',
  assetPlantNo: 'Asset / Plant No.',
  testIssueDate: 'Test / Issue Date',
  intervalMonths: 'Interval (Months)',
  expiryDate: 'Expiry Date',
  process: 'Process',
  outputMode: 'Output Mode',
  grade: 'Grade',
  verificationRange: 'Verification Range',
  rangeRecorded: 'Range Recorded',
  supplyVoltage: 'Supply Voltage (V)',
  supplyFrequency: 'Supply Frequency',
  wfsUnit: 'WFS Unit',
  ambientTemp: 'Ambient Temp (°C)',
  remarks: 'Remarks',
  profileName: 'Profile Name',
  equipmentType: 'Equipment Type',
  manufacturer: 'Manufacturer',
  model: 'Model',
  currentMinA: 'Current Min (A)',
  currentMaxA: 'Current Max (A)',
  ratedNoLoadU0V: 'Rated No-Load U0 (V)',
  voltageMinV: 'Voltage Min (V)',
  voltageMaxV: 'Voltage Max (V)',
  wfsMin: 'WFS Min (m/min)',
  wfsMax: 'WFS Max (m/min)',
  defaultGrade: 'Default Grade',
  defaultProcess: 'Default Process',
  defaultOutputMode: 'Default Output Mode',
  defaultVerificationRange: 'Default Verification Range',
  defaultValueClassification: 'Default Value Classification',
  defaultFrequency: 'Default Frequency',
  registerNote: 'Register Note',
  active: 'Active',
  parameter: 'Parameter',
  method: 'Method',
  rangeCondition: 'Range Condition',
  toleranceType: 'Tolerance Type',
  toleranceValue: 'Tolerance Value',
  toleranceBasis: 'Tolerance Basis',
  lowRangeThresholdPct: 'Low Range Threshold (%)',
  displayOrder: 'Display Order',
  notes: 'Notes',
  valueClassification: 'Value Classification',
  permittedTolerance: 'Permitted Tolerance',
  sectionResult: 'Section Result',
  technician: 'Technician',
  supervisor: 'Supervisor',
};

/** Test-point grid column headers — Title Case. */
export const AW_TEST_POINT_COLUMNS = {
  point: 'Point',
  setControl: 'Set / Control',
  controlPosition: 'Control Position',
  firstTestReference: 'First-Test Reference',
  machineDisplay: 'Machine Display',
  todayMeterReading: 'Today Meter Reading',
  absError: 'Abs Error',
  result: 'Result',
};

/** Compact headers for narrow / multi-column test-point tables. */
export const AW_TEST_POINT_COLUMNS_SHORT = {
  point: 'Pt',
  setControl: 'Set / Ctrl',
  controlPosition: 'Ctrl Pos',
  firstTestReference: '1st Ref',
  machineDisplay: 'Display',
  todayMeterReading: 'Reading',
  absError: 'Error',
  result: 'Result',
};

/** List / filter toolbar labels — Title Case. */
export const AW_FILTER_LABELS = {
  search: 'Search',
  project: 'Project',
  status: 'Status',
  expiringWithin: 'Expiring Within',
  equipmentType: 'Equipment Type',
  parameter: 'Parameter',
};

export const AW_PARAMETER_LABELS = {
  Current: 'Current',
  Voltage: 'Voltage',
  'Wire feed speed': 'Wire Feed Speed',
};

export const PARAMETER_CLASSIFICATION_KEYS = {
  Current: 'currentValueClassification',
  Voltage: 'voltageValueClassification',
  'Wire feed speed': 'wfsValueClassification',
};

/** BE proceduralConfirmations keys */
export const PROCEDURAL_CONFIRMATION_FIELDS = [
  { key: 'loadSetup', label: 'Load / Setup Verified Per Procedure' },
  { key: 'stableOutput', label: 'Stable Output Achieved Before Readings' },
  { key: 'traceableInstruments', label: 'Reference Instruments Are Traceable and in Date' },
  { key: 'personnelQualified', label: 'Personnel Qualified for Verification' },
];

const PROCEDURAL_CONFIRMATION_KEYS = PROCEDURAL_CONFIRMATION_FIELDS.map((f) => f.key);

const toProceduralBool = (value) =>
  value === true || value === 1 || value === '1' || String(value).toLowerCase() === 'true';

/** Normalize procedural flags from API (handles missing keys / string booleans). */
export const normalizeProceduralConfirmationsFromApi = (raw = {}) => {
  const source = raw && typeof raw === 'object' ? raw : {};
  return PROCEDURAL_CONFIRMATION_KEYS.reduce((acc, key) => {
    acc[key] = toProceduralBool(source[key]);
    return acc;
  }, {});
};

/** Always send all four procedural keys as booleans on PUT. */
export const buildProceduralConfirmationsPayload = (procedural = {}) =>
  PROCEDURAL_CONFIRMATION_KEYS.reduce((acc, key) => {
    acc[key] = toProceduralBool(procedural[key]);
    return acc;
  }, {});

/** Resolve siteCode ↔ siteName when API returns only one field. */
export const resolveSiteFields = (certificate = {}, sites = []) => {
  let siteCode = certificate.siteCode ?? certificate.crcSiteCode;
  let siteName = certificate.siteName ?? '';
  const list = Array.isArray(sites) ? sites : [];

  if (!siteCode && siteName && list.length) {
    const match = list.find(
      (s) =>
        String(s.siteName ?? s.name ?? '').toLowerCase() === String(siteName).toLowerCase()
    );
    siteCode = match?.siteCode ?? match?.code;
  }
  if (siteCode && !siteName && list.length) {
    const match = list.find((s) => String(s.siteCode ?? s.code) === String(siteCode));
    siteName = match?.siteName ?? match?.name ?? '';
  }
  return {
    siteCode: siteCode != null && siteCode !== '' ? String(siteCode) : undefined,
    siteName: siteName || undefined,
  };
};

export const findAuthorisationByRole = (authorisations, role) => {
  const list = Array.isArray(authorisations) ? authorisations : [];
  return list.find(
    (entry) => String(entry?.role ?? '').toLowerCase() === String(role).toLowerCase()
  );
};

/** Unwrap nested certificate payloads from GET/PUT responses. */
export const normalizeCertificateDetail = (payload) => {
  if (!payload || typeof payload !== 'object') return payload;
  if (payload.certificate && typeof payload.certificate === 'object') return payload.certificate;
  if (payload.data && typeof payload.data === 'object' && !payload.id) return payload.data;
  return payload;
};

/** Human-readable help for common blockingReasons from BE. */
export const AW_WORKFLOW_REQUIREMENTS = {
  save: [
    'Project, CRC Site, Serial Number, Test / Issue Date, and Ambient Temp',
    'Reference instruments and signatures are saved on Save (PUT)',
  ],
  submitForReview: [
    'Technician name and signature',
    'All four procedural confirmations checked',
    'Test points entered for each active parameter (not "Not tested")',
  ],
  authorise: [
    'Certificate status must be Awaiting Review',
    'Overall verification result must be Pass (all test points pass)',
    'Supervisor name and signature',
    'Ambient temperature 15–35 °C',
    'All procedural confirmations checked',
  ],
};

export const AW_WORKFLOW_NOTICE_COPY = {
  draft: {
    title: 'Still required',
    intro: 'Fix these items, then Save. Technician signature is required before Submit for Review.',
  },
  authorise: {
    title: 'Still required',
    intro: 'Fix these items before Authorise & Issue.',
  },
};

const dedupeWorkflowReasons = (reasons = []) =>
  [...new Set((reasons ?? []).map((r) => String(r).trim()).filter(Boolean))];

/** Draft save vs Awaiting Review authorise — never merge both blocker lists. */
export const getCertificateWorkflowNotice = (certificate) => {
  const status = certificate?.status ?? AW_CERTIFICATE_STATUS.DRAFT;

  if (status === AW_CERTIFICATE_STATUS.DRAFT) {
    const reasons = dedupeWorkflowReasons(certificate?.blockingReasons);
    if (
      certificate?.requiresTechnicianSignature
      && !reasons.some((r) => String(r).toLowerCase().includes('technician'))
    ) {
      reasons.push('Technician name and signature required');
    }
    return {
      ...AW_WORKFLOW_NOTICE_COPY.draft,
      reasons,
      context: 'draft',
    };
  }

  if (status === AW_CERTIFICATE_STATUS.AWAITING_REVIEW) {
    const reasons = dedupeWorkflowReasons(certificate?.authoriseBlockingReasons);
    if (
      certificate?.requiresSupervisorSignature
      && !reasons.some((r) => String(r).toLowerCase().includes('supervisor'))
    ) {
      reasons.push('Supervisor signature is required');
    }
    return {
      ...AW_WORKFLOW_NOTICE_COPY.authorise,
      reasons,
      context: 'authorise',
    };
  }

  return { title: '', intro: '', reasons: [], context: null };
};

/** Prefer mutation response body; fall back to GET when the API returns no id. */
export const resolveCertificateDetail = async (response, id, fetchDetail) => {
  const detail = normalizeCertificateDetail(response);
  if (detail?.id) return detail;
  if (id && fetchDetail) return fetchDetail(id);
  return detail;
};

export const formatBlockingReasonHelp = (reason) => {
  const r = String(reason ?? '').toLowerCase();
  if (r.includes('traceableinstruments') || r.includes('personnelqualified') || r.includes('procedural')) {
    return `${reason} — Check all four boxes under Procedural Confirmations, then Save.`;
  }
  if (r.includes('supervisor') && r.includes('signature')) {
    return `${reason} — Required for Authorise & Issue (not for Save). Supervisor signs when status is Awaiting Review.`;
  }
  if (r.includes('overall') && r.includes('pass')) {
    return `${reason} — Enter test readings and Save; BE recalculates Pass/Fail from equipment profile + criteria.`;
  }
  if (r.includes('technician')) {
    return `${reason} — Enter Technician name and Add Signature, then Save.`;
  }
  return reason;
};

/** Short actionable line for the blocking banner (no long explanations). */
export const formatBlockingReasonShort = (reason) => {
  const r = String(reason ?? '').toLowerCase();
  if (r.includes('traceableinstruments') || r.includes('personnelqualified') || r.includes('procedural')) {
    return 'Check all four Procedural Confirmations';
  }
  if (r.includes('supervisor') && r.includes('signature')) {
    return 'Add Supervisor name and signature';
  }
  if (r.includes('technician')) {
    return 'Add Technician name and signature';
  }
  if (r.includes('overall') && r.includes('pass')) {
    return 'Overall result must be Pass — enter test readings and Save';
  }
  if (r.includes('ambient')) {
    return 'Ambient temperature must be 15–35 °C';
  }
  if (r.includes('test point') || r.includes('testpoint')) {
    return 'Complete test points for each active parameter';
  }
  if (r.includes('draft') || r.includes('awaiting review')) {
    return String(reason).trim();
  }
  const text = String(reason ?? '').trim();
  const dash = text.indexOf(' — ');
  return dash > -1 ? text.slice(0, dash).trim() : text;
};

export const hasSavedAuthorisationSignature = (auth = {}) =>
  Boolean(
    auth.signatureDataUrl
    || auth.signatureBase64
    || auth.signatureS3Key
    || auth.signatureUrl
    || (typeof auth.signature === 'string' && /^https?:\/\//i.test(auth.signature))
  );

const isPresignedSignatureUrl = (value) =>
  typeof value === 'string' && /^https?:\/\//i.test(value);

/** Presigned view URL from GET certificate detail (DPR-style `authorisations[].signature`). */
export const resolveAuthorisationSignatureViewUrl = (auth = {}) => {
  if (isPresignedSignatureUrl(auth.signature)) return auth.signature;
  return (
    auth.signatureUrl
    ?? auth.signatureFileUrl
    ?? auth.presignedSignatureUrl
    ?? auth.signatureDataUrl
    ?? auth.s3Object?.objectUrl
    ?? null
  );
};

export const getResultBannerStyle = (result) => {
  if (result === AW_OVERALL_RESULT.PASS) {
    return { backgroundColor: CERTIFICATE_CELL_COLORS.pass, color: '#fff' };
  }
  if (result === AW_OVERALL_RESULT.FAIL) {
    return { backgroundColor: CERTIFICATE_CELL_COLORS.fail, color: '#fff' };
  }
  return { backgroundColor: CERTIFICATE_CELL_COLORS.incomplete, color: '#1e293b' };
};

export const getOverallResultPillClass = (result) => {
  if (result === AW_OVERALL_RESULT.PASS) return 'aw-status-pill--pass';
  if (result === AW_OVERALL_RESULT.FAIL) return 'aw-status-pill--fail';
  return 'aw-status-pill--incomplete';
};

export const getCertificateStatusPillClass = (status) => {
  switch (status) {
    case AW_CERTIFICATE_STATUS.ISSUED:
      return 'aw-status-pill--issued';
    case AW_CERTIFICATE_STATUS.AWAITING_REVIEW:
      return 'aw-status-pill--review';
    case AW_CERTIFICATE_STATUS.SUPERSEDED:
      return 'aw-status-pill--superseded';
    default:
      return 'aw-status-pill--draft';
  }
};

export const getPointResultStyle = (result) => {
  if (result === 'Pass') return { backgroundColor: CERTIFICATE_CELL_COLORS.pass, color: '#fff' };
  if (result === 'Fail') return { backgroundColor: CERTIFICATE_CELL_COLORS.fail, color: '#fff' };
  return { backgroundColor: CERTIFICATE_CELL_COLORS.auto };
};

export const isCertificateEditable = (status) =>
  status === AW_CERTIFICATE_STATUS.DRAFT || status === AW_CERTIFICATE_STATUS.AWAITING_REVIEW;

export const isCertificateViewOnly = (status) =>
  status === AW_CERTIFICATE_STATUS.ISSUED || status === AW_CERTIFICATE_STATUS.SUPERSEDED;

/** BE pagination: prefer rows + count */
export const normalizePagedResponse = (payload) => {
  const data = payload?.data ?? payload ?? {};
  const rows = data.rows ?? data.items ?? data.list ?? (Array.isArray(data) ? data : []);
  return {
    items: Array.isArray(rows) ? rows : [],
    total: data.count ?? data.total ?? (Array.isArray(rows) ? rows.length : 0),
    pageNumber: data.pageNumber ?? data.page ?? 1,
    pageSize: data.pageSize ?? data.limit ?? 20,
  };
};

export const methodForClassification = (classification) => {
  if (classification === 'Displayed value - Digital/Analogue meter') return AW_METHODS.CALIBRATION;
  if (classification === 'Set value - Physical units') return AW_METHODS.VALIDATION;
  if (classification === 'Set control - Arbitrary units') return AW_METHODS.CONSISTENCY;
  return AW_METHODS.NOT_TESTED;
};

export const mapReferenceInstrumentFromApi = (row = {}, index = 0) => ({
  id: row.id,
  key: row.id ?? `ref-${index}`,
  instrumentName: row.instrumentName ?? row.name ?? '',
  serialNumber: row.serialNumber ?? row.serial ?? '',
  calibrationCertNo:
    row.calibrationCertNo
    ?? row.calibrationCertificateNo
    ?? row.certificateNumber
    ?? row.certificateNo
    ?? '',
  calibrationExpiryDate: row.calibrationExpiryDate ?? row.calExpiryDate ?? null,
  displayOrder: row.displayOrder ?? index,
});

export const mapReferenceInstrumentToApi = (row = {}, index = 0) => {
  const payload = { displayOrder: row.displayOrder ?? index };
  if (row.id != null && row.id !== '') payload.id = row.id;

  const instrumentName = String(row.instrumentName ?? row.name ?? '').trim();
  if (instrumentName) {
    payload.instrumentName = instrumentName;
    payload.name = instrumentName;
  }

  const serialNumber = String(row.serialNumber ?? row.serial ?? '').trim();
  if (serialNumber) payload.serialNumber = serialNumber;

  const calibrationCertNo = String(
    row.calibrationCertNo ?? row.certificateNumber ?? row.certificateNo ?? ''
  ).trim();
  if (calibrationCertNo) {
    payload.calibrationCertNo = calibrationCertNo;
    payload.calibrationCertificateNo = calibrationCertNo;
  }

  const expiry = row.calibrationExpiryDate ?? row.calExpiryDate;
  if (expiry) {
    const formatted = moment(expiry).isValid()
      ? moment(expiry).format('YYYY-MM-DD')
      : expiry;
    payload.calibrationExpiryDate = formatted;
  }

  return payload;
};

export const mapAuthorisationFromApi = (auth = {}, fallbackName) => {
  const rawSignature = auth.signature;
  const signatureBase64 =
    auth.signatureBase64
    ?? (rawSignature && !isPresignedSignatureUrl(rawSignature) ? rawSignature : null);
  const signatureUrl = resolveAuthorisationSignatureViewUrl(auth);
  const signatureS3Key = auth.signatureS3Key ?? auth.s3Object?.key ?? null;
  const signatureDataUrl =
    signatureUrl
    ?? (signatureBase64
      ? (String(signatureBase64).startsWith('data:')
        ? signatureBase64
        : `data:image/png;base64,${signatureBase64}`)
      : null);
  return {
    name: auth.name ?? fallbackName ?? '',
    signatureBase64,
    signatureS3Key,
    signatureUrl,
    signatureDataUrl,
    signedAt: auth.signedAt ?? null,
  };
};

/** Keep local name/signature preview after save when API omits authorisations in PUT response. */
export const mergeAuthorisationFromApi = (apiAuth = {}, prevAuth = {}, fallbackName) => {
  const mapped = mapAuthorisationFromApi(apiAuth, fallbackName ?? prevAuth?.name);
  const merged = { ...mapped };
  const prevName = String(prevAuth?.name ?? '').trim();
  if (!String(merged.name ?? '').trim() && prevName) {
    merged.name = prevAuth.name;
  }
  if (merged.signatureDataUrl || !prevAuth?.signatureDataUrl) {
    return merged;
  }
  const hasStoredSignature =
    merged.signatureS3Key
    || merged.signedAt
    || prevAuth.signatureS3Key
    || prevAuth.signatureBase64;
  if (!hasStoredSignature) return merged;
  return {
    ...merged,
    signatureDataUrl: prevAuth.signatureDataUrl,
    signatureBase64: prevAuth.signatureBase64 ?? merged.signatureBase64,
  };
};

export const resolveAuthorisationSignatureUrl = (payload) =>
  resolveAuthorisationSignatureViewUrl(payload)
  ?? resolvePdfUrl(payload)
  ?? payload?.url
  ?? null;

export const mapAuthorisationToApi = (role, auth = {}) => {
  const name = String(auth.name ?? '').trim();
  const payload = {
    role,
    name: name || null,
  };
  if (auth.signatureBase64) {
    const raw = String(auth.signatureBase64);
    payload.signatureBase64 = raw.startsWith('data:') ? raw : `data:image/png;base64,${raw}`;
    payload.signedAt = auth.signedAt ?? new Date().toISOString();
  } else if (auth.signatureS3Key) {
    payload.signatureS3Key = auth.signatureS3Key;
    if (auth.signedAt) payload.signedAt = auth.signedAt;
  }
  return payload;
};

/** PUT save — Technician signature; supervisor name only (signature via /authorise). */
export const buildSaveAuthorisationsPayload = (
  technicianAuth = {},
  supervisorAuth = {},
  status = AW_CERTIFICATE_STATUS.DRAFT
) => {
  const authorisations = [];
  const technician = mapAuthorisationToApi('Technician', technicianAuth);
  if (technician.name || technician.signatureBase64 || technician.signatureS3Key) {
    authorisations.push(technician);
  }
  if (status === AW_CERTIFICATE_STATUS.AWAITING_REVIEW) {
    const supervisorName = String(supervisorAuth?.name ?? '').trim();
    if (supervisorName) {
      const row = { role: 'Supervisor', name: supervisorName };
      // Existing stored signature only — new drawings go via POST /authorise, not PUT.
      if (supervisorAuth.signatureS3Key && !supervisorAuth.signatureBase64) {
        row.signatureS3Key = supervisorAuth.signatureS3Key;
        if (supervisorAuth.signedAt) row.signedAt = supervisorAuth.signedAt;
      }
      authorisations.push(row);
    }
  }
  return authorisations;
};

export const buildSupervisorAuthorisationPayload = (supervisorAuth = {}) => {
  const supervisor = mapAuthorisationToApi('Supervisor', supervisorAuth);
  if (supervisor.name || supervisor.signatureBase64 || supervisor.signatureS3Key) {
    return [supervisor];
  }
  return [];
};

export const buildAuthoriseRequestBody = (siteCode, supervisorAuth = {}) => ({
  siteCode: siteCode ? String(siteCode) : null,
  authorisations: buildSupervisorAuthorisationPayload(supervisorAuth),
});

/** @deprecated Use buildSaveAuthorisationsPayload */
export const buildAuthorisationsPayload = (technicianAuth = {}, supervisorAuth = {}) =>
  buildSaveAuthorisationsPayload(technicianAuth, supervisorAuth);

/** Format section-header permitted tolerance from parameterBlocks (BE auto-calculated). */
export const formatPermittedToleranceDisplay = ({
  permittedTolerance,
  permittedToleranceLow,
  permittedToleranceHigh,
} = {}) => {
  if (permittedTolerance != null && permittedTolerance !== '') {
    return String(permittedTolerance);
  }
  const low = permittedToleranceLow;
  const high = permittedToleranceHigh;
  if (low != null && high != null && low !== high) {
    return `${low} – ${high}`;
  }
  const single = high ?? low;
  return single != null && single !== '' ? String(single) : '';
};

export const AW_REQUIRED_FIELD_LABELS = {
  projectId: AW_FORM_LABELS.project,
  siteCode: AW_FORM_LABELS.crcSite,
  serialNumber: AW_FORM_LABELS.serialNumber,
  testIssueDate: AW_FORM_LABELS.testIssueDate,
  ambientTempC: AW_FORM_LABELS.ambientTemp,
};

/** Turn Ant Design validateFields errors into user-facing field labels. */
export const formatAntdValidationErrors = (error, fieldLabels = AW_REQUIRED_FIELD_LABELS) => {
  if (!error?.errorFields?.length) return [];
  return [...new Set(
    error.errorFields.map((field) => {
      const name = Array.isArray(field.name) ? field.name[field.name.length - 1] : field.name;
      const label = fieldLabels[name] ?? name;
      const firstError = field.errors?.[0];
      if (firstError && firstError !== 'Required') return firstError;
      return label;
    })
  )];
};

/** Notification title + description for save/validation failures. */
export const formatSaveValidationNotification = (err, fieldLabels = AW_REQUIRED_FIELD_LABELS) => {
  const missingFields = formatAntdValidationErrors(err, fieldLabels);
  if (missingFields.length) {
    return {
      message: 'Please fill all required fields',
      description: missingFields.join(', '),
      isValidation: true,
    };
  }
  const apiMessage =
    err?.response?.data?.error
    ?? err?.response?.data?.message
    ?? err?.message
    ?? String(err);
  return {
    message: 'Unable to save',
    description: apiMessage,
    isValidation: false,
  };
};

export const findParameterBlock = (parameterBlocks, parameter) => {
  if (Array.isArray(parameterBlocks)) {
    return parameterBlocks.find((b) => b.parameter === parameter) ?? {};
  }
  if (parameterBlocks && typeof parameterBlocks === 'object') {
    return parameterBlocks[parameter] ?? {};
  }
  return {};
};

export const mapTestPointFromApi = (point = {}) => ({
  pointNo: point.pointNo,
  machineSettingOrDisplay: point.machineSettingOrDisplay,
  machineDisplayValue: point.machineSettingOrDisplay,
  setOrControlValue: point.machineSettingOrDisplay,
  firstTestReferenceValue: point.firstTestReference ?? point.firstTestReferenceReading ?? point.firstTestReferenceValue,
  todayMeterReading: point.todayMeterReading,
  absError: point.absoluteError ?? point.absError,
  result: point.pointResult ?? point.result,
});

export const mapTestPointToApi = (point = {}, parameter) => {
  const apiPoint = {
    pointNo: point.pointNo,
    todayMeterReading: point.todayMeterReading,
  };
  if (parameter) apiPoint.parameter = parameter;
  const setting =
    point.machineSettingOrDisplay ?? point.machineDisplayValue ?? point.setOrControlValue;
  if (setting != null && setting !== '') apiPoint.machineSettingOrDisplay = setting;
  const firstRef = point.firstTestReference ?? point.firstTestReferenceReading ?? point.firstTestReferenceValue;
  if (firstRef != null && firstRef !== '') {
    apiPoint.firstTestReference = firstRef;
    apiPoint.firstTestReferenceReading = firstRef;
  }
  return apiPoint;
};

/** Flat testPoints[] for single PUT save (replaces per-parameter POST /test-points). */
export const buildTestPointsPayload = (parameterState = {}) => {
  const testPoints = [];
  AW_PARAMETERS.forEach((parameter) => {
    const block = parameterState[parameter];
    (block?.points ?? []).forEach((point) => {
      if (point == null) return;
      testPoints.push(mapTestPointToApi(point, parameter));
    });
  });
  return testPoints;
};

export const resolvePdfUrl = (result) =>
  result?.downloadUrl
  ?? result?.presignedUrl
  ?? result?.fileUrl
  ?? result?.pdfFileUrl
  ?? result?.pdfUrl
  ?? result?.url
  ?? (typeof result === 'string' ? result : null);

export const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const raw = String(reader.result || '');
      const base64 = raw.includes(',') ? raw.split(',')[1] : raw;
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export const openPdfUrl = (url) => {
  if (!url) return;
  window.open(url, '_blank', 'noopener,noreferrer');
};
